/**
 * Panel procesu zatrudnienia w profilu pracownika (spec 01 §3, PLAN §6.1).
 *
 * Przyciski biorą się z listy `actions` liczonej przez maszynę stanów na
 * serwerze — UI nie zgaduje, co wolno zrobić, i nie pokazuje akcji, która
 * i tak skończyłaby się konfliktem.
 *
 * Formularz warunków umowy nie ma wzorca w makiecie [D11]; pola odpowiadają
 * tabeli warunków z portalu pracownika (spec 04, ekran 3).
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  FormField,
  InfoBanner,
  Input,
  Modal,
  ProcessTimeline,
  Select,
  Spinner,
  cx,
  useToast,
} from '@k1p4/ui'
import { CONTRACT_TYPES, ONBOARDING_STAGES, isEmploymentContract, warsawDay } from '@k1p4/shared'
import { formatDate, formatPLN } from '@k1p4/i18n'
import {
  ApiError,
  api,
  type ContractType,
  type EmployeeOnboardingResponse,
} from '../../api/client'
import styles from './OnboardingPanel.module.css'

/** Akcje HR w kolejności procesu — panel pokazuje wyłącznie te dostępne. */
const ACTION_ORDER = [
  'approve_data',
  'return_data',
  'prepare_terms',
  'issue_referral',
  'approve_medical',
  'confirm_bhp',
  'answer_question',
] as const

type HrAction = (typeof ACTION_ORDER)[number]

/** Akcje wymagające formularza — reszta wykonuje się jednym kliknięciem. */
const FORM_ACTIONS: readonly HrAction[] = [
  'return_data',
  'prepare_terms',
  'issue_referral',
  'confirm_bhp',
  'answer_question',
]

const DEFAULT_REFERRAL_DAYS = '14'

/** Odmowy z nazwą warunku (Fala 6, grupa 31) — panel mówi, czego brakuje. */
const CONFLICT_MESSAGES: Record<string, string> = {
  MEDICAL_NOT_APPROVED: 'hrProcess.errorMedicalNotApproved',
  MEDICAL_NOT_CONFIRMED: 'hrProcess.errorMedicalNotConfirmed',
  MEDICAL_ALREADY_APPROVED: 'hrProcess.errorMedicalApproved',
  MEDICAL_EXPIRES_BEFORE_START: 'hrProcess.errorMedicalExpiresBeforeStart',
  MEDICAL_STILL_VALID: 'hrProcess.errorMedicalStillValid',
}

type Medical = NonNullable<EmployeeOnboardingResponse['medical']>

/**
 * Badanie sprzed skierowania (Fala 6, grupa 120) — nie odmowa, tylko sygnał
 * dla kadr: bywa aktualnym orzeczeniem od poprzedniego pracodawcy
 * (art. 229 § 1(1) k.p.), ale bywa też pomyłką w dacie.
 */
function examBeforeReferral(medical: Medical): boolean {
  if (!medical.examDate || !medical.referralIssuedAt) return false
  return medical.examDate < warsawDay(new Date(medical.referralIssuedAt))
}

interface TermsForm {
  position: string
  fte: string
  startDate: string
  /** Pusty, dopóki kadry go nie wybiorą — bez cichego „okres próbny" (grupa 116). */
  contractType: ContractType | ''
  probationMonths: string
  baseSalaryZl: string
  bonusNote: string
  workplace: string
  workMode: string
}

export function OnboardingPanel({
  employeeId,
  employmentEndedOn = null,
}: {
  employeeId: string
  /** Dzień ustania zatrudnienia ('' — bez daty); `null`, gdy osoba pracuje (grupa 105). */
  employmentEndedOn?: string | null
}) {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const ids = {
    comment: useId(),
    answer: useId(),
    days: useId(),
    bhpDate: useId(),
    bhpValid: useId(),
    bhpBy: useId(),
    position: useId(),
    fte: useId(),
    startDate: useId(),
    contractType: useId(),
    probation: useId(),
    salary: useId(),
    bonus: useId(),
    workplace: useId(),
    workMode: useId(),
  }

  const process = useQuery({
    queryKey: ['employees', employeeId, 'onboarding'],
    queryFn: () => api.employeeOnboarding(employeeId),
  })

  const [openAction, setOpenAction] = useState<HrAction | null>(null)
  const [comment, setComment] = useState('')
  const [days, setDays] = useState(DEFAULT_REFERRAL_DAYS)
  const [bhp, setBhp] = useState({ performedAt: '', validUntil: '', conductedBy: '' })
  const [terms, setTerms] = useState<TermsForm>({
    position: '',
    fte: '1',
    startDate: '',
    contractType: '',
    probationMonths: '',
    baseSalaryZl: '',
    bonusNote: '',
    workplace: '',
    workMode: '',
  })
  const [error, setError] = useState<string | null>(null)
  /* Pola odrzucone przez serwer — zaznaczone przy polu, nie tylko zdaniem (grupa 117). */
  const [termsIssues, setTermsIssues] = useState<readonly string[]>([])

  function closeForm(): void {
    setOpenAction(null)
    setComment('')
    setError(null)
    setTermsIssues([])
  }

  const run = useMutation({
    mutationFn: async (
      action: HrAction,
    ): Promise<{
      accountCreated?: boolean
      accountMailed?: boolean
      notifiedEmployee?: boolean
      stage?: string
      sideState?: string | null
    }> => {
      if (action === 'approve_data') return api.approveEmployeeData(employeeId)
      if (action === 'approve_medical') return api.approveMedical(employeeId)
      if (action === 'return_data') return api.returnEmployeeData(employeeId, comment.trim())
      if (action === 'answer_question') return api.answerOnboardingQuestion(employeeId, comment.trim())
      if (action === 'issue_referral') return api.issueReferral(employeeId, Number(days))
      if (action === 'confirm_bhp') {
        return api.confirmBhp(employeeId, {
          performedAt: bhp.performedAt,
          conductedBy: bhp.conductedBy.trim(),
          ...(bhp.validUntil ? { validUntil: bhp.validUntil } : {}),
        })
      }
      if (terms.contractType === '') throw new Error('contract type missing')
      const employment = isEmploymentContract(terms.contractType)
      return api.prepareTerms(employeeId, {
        position: terms.position.trim(),
        /* Etat i okres próbny tylko tam, gdzie istnieją (art. 25 § 2, art. 29 § 1 k.p.). */
        ...(employment ? { fte: Number(terms.fte) } : {}),
        startDate: terms.startDate,
        contractType: terms.contractType,
        probationMonths:
          terms.contractType === 'uop_probny' && terms.probationMonths
            ? Number(terms.probationMonths)
            : null,
        /* UI zbiera złote, API i baza liczą w groszach (CLAUDE.md). */
        baseSalaryGr: Math.round(Number(terms.baseSalaryZl.replace(',', '.')) * 100),
        bonusNote: terms.bonusNote.trim() || null,
        workplace: terms.workplace.trim() || null,
        workMode: terms.workMode.trim() || null,
      })
    },
    onSuccess: (result) => {
      /*
       * Komunikat mówi to, co się stało (Fala 6, grupy 90 i 138): zatwierdzenie
       * danych czy orzeczenia poczty nie wysyła, a wiadomość do pracownika
       * mogła nie wyjść — serwer mówi to polem `notifiedEmployee`.
       */
      const message = result.accountCreated
        ? result.accountMailed === false
          ? 'hrProcess.accountCreatedNotMailed'
          : 'hrProcess.accountCreated'
        : result.notifiedEmployee === true
          ? 'hrProcess.savedNotified'
          : result.notifiedEmployee === false
            ? 'hrProcess.savedNotNotified'
            : 'hrProcess.saved'
      toast.show(t(message), {
        tone: result.notifiedEmployee === false || result.accountMailed === false ? 'info' : 'success',
      })
      closeForm()
      void queryClient.invalidateQueries({ queryKey: ['employees', employeeId] })
      /*
       * Profil używa klucza ['employee', id] (liczba pojedyncza) — bez tej
       * inwalidacji pigułki statusu i oś procesu stałyby w miejscu do
       * przeładowania strony, mimo że proces poszedł dalej.
       */
      void queryClient.invalidateQueries({ queryKey: ['employee', employeeId] })
    },
    onError: (caught) => {
      if (caught instanceof ApiError && caught.status === 400 && caught.issues.length > 0) {
        setTermsIssues(caught.issues)
        setError('hrProcess.termsInvalid')
        return
      }
      setError(
        caught instanceof ApiError && caught.status === 409
          ? (CONFLICT_MESSAGES[caught.code] ?? 'hrProcess.errorConflict')
          : 'hrProcess.errorGeneric',
      )
    },
  })

  if (process.isPending) return <Spinner label={t('common.loading')} />
  if (process.isError || !process.data) return null

  const data = process.data
  const actions = ACTION_ORDER.filter((action) => data.actions.includes(action))
  /* Orzeczenie czeka na decyzję kadr — pokazujemy, co pracownik wpisał (grupa 120). */
  const pendingMedical =
    data.actions.includes('approve_medical') && data.medical?.examDate && data.medical.validUntil
      ? data.medical
      : null

  function start(action: HrAction): void {
    setError(null)
    if (FORM_ACTIONS.includes(action)) {
      setTermsIssues([])
      if (action === 'prepare_terms' && data.terms) {
        /* Poprawa warunków startuje od poprzedniej propozycji, nie od pustego formularza. */
        setTerms({
          position: data.terms.position ?? '',
          fte: data.terms.fte,
          startDate: data.terms.startDate ?? '',
          contractType: data.terms.contractType ?? '',
          probationMonths: data.terms.probationMonths?.toString() ?? '',
          baseSalaryZl:
            data.terms.baseSalaryGr !== null ? (data.terms.baseSalaryGr / 100).toFixed(2) : '',
          bonusNote: data.terms.bonusNote ?? '',
          workplace: data.terms.workplace ?? '',
          workMode: data.terms.workMode ?? '',
        })
      } else if (action === 'prepare_terms' && data.defaults) {
        /* Pierwsze warunki startują od planu z zaproszenia i akt (grupy 33 i 116). */
        const defaults = data.defaults
        setTerms((current) => ({
          ...current,
          position: current.position || (defaults.position ?? ''),
          startDate: current.startDate || (defaults.startDate ?? ''),
          contractType: current.contractType || (defaults.contractType ?? ''),
        }))
      }
      setOpenAction(action)
      return
    }
    run.mutate(action)
  }

  const employmentTerms = terms.contractType !== '' && isEmploymentContract(terms.contractType)
  const probationTerms = terms.contractType === 'uop_probny'
  const termsIncomplete =
    terms.position.trim().length === 0 ||
    terms.startDate.length === 0 ||
    terms.contractType === '' ||
    (employmentTerms && !(Number(terms.fte) >= 0.1 && Number(terms.fte) <= 1)) ||
    (probationTerms && terms.probationMonths.trim().length === 0) ||
    terms.baseSalaryZl.trim().length === 0
  /** Błąd przy polu z odpowiedzi serwera — klucz pola jak w schemacie API. */
  const termsIssue = (field: string): { error: string } | Record<string, never> =>
    termsIssues.includes(field) ? { error: t(`hrProcess.termsFieldErrors.${field}`) } : {}
  const salaryLabel =
    terms.contractType === '' || employmentTerms
      ? 'hrProcess.termsSalaryBy.employment'
      : `hrProcess.termsSalaryBy.${terms.contractType}`

  return (
    <Card className={styles.panel} pad="lg">
      <div className={styles.head}>
        <h3 className={styles.title}>{t('hrProcess.title')}</h3>
        <span className={styles.stage}>
          {t('hrProcess.stageLabel', {
            current: data.stageNumber,
            total: ONBOARDING_STAGES.length,
          })}
        </span>
      </div>

      {data.sideState === 'question' ? (
        <InfoBanner tone="warning" className={styles.banner}>
          {t('hrProcess.questionBanner')}
        </InfoBanner>
      ) : null}
      {data.sideState === 'rejected' ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t('hrProcess.rejectedBanner')}
        </InfoBanner>
      ) : null}
      {data.invitation && data.invitation.status === 'pending' ? (
        <p className={styles.hint}>
          {t('hrProcess.invitationPending', {
            date: formatDate(new Date(data.invitation.expiresAt), 'dot'),
            email: data.invitation.email,
          })}
        </p>
      ) : null}
      {data.terms && data.terms.baseSalaryGr !== null && data.terms.startDate ? (
        <p className={styles.hint}>
          {t('hrProcess.currentTerms', {
            salary: formatPLN(data.terms.baseSalaryGr),
            date: formatDate(data.terms.startDate, 'dot'),
          })}
        </p>
      ) : null}
      {pendingMedical ? (
        <>
          <p className={styles.hint}>
            {t('hrProcess.medicalSummary', {
              examDate: formatDate(pendingMedical.examDate!, 'dot'),
              validUntil: formatDate(pendingMedical.validUntil!, 'dot'),
            })}
          </p>
          {examBeforeReferral(pendingMedical) ? (
            <InfoBanner tone="warning" className={styles.banner}>
              {t('hrProcess.medicalBeforeReferral', {
                examDate: formatDate(pendingMedical.examDate!, 'dot'),
                referralDate: formatDate(warsawDay(new Date(pendingMedical.referralIssuedAt!)), 'dot'),
              })}
            </InfoBanner>
          ) : null}
          {/* Osoba aktywowana przed Falą 6 z orzeczeniem niezatwierdzonym — zatwierdzenie po fakcie. */}
          {data.stage === 'active' ? (
            <>
              <InfoBanner tone="warning" className={styles.banner}>
                {t('hrProcess.medicalLate')}
              </InfoBanner>
              <div className={styles.actions}>
                <Button size="sm" disabled={run.isPending} onClick={() => start('approve_medical')}>
                  {t('hrProcess.actions.approve_medical')}
                </Button>
              </div>
            </>
          ) : null}
        </>
      ) : null}

      {error !== null && openAction === null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
        </InfoBanner>
      ) : null}

      {data.stage === 'active' && employmentEndedOn !== null ? (
        <p className={styles.hint}>
          {t('hrProcess.doneEnded', {
            date: employmentEndedOn ? formatDate(employmentEndedOn, 'dot') : '—',
          })}
        </p>
      ) : data.stage === 'active' ? (
        <p className={styles.done}>{t('hrProcess.done')}</p>
      ) : actions.length === 0 ? (
        <p className={styles.hint}>{t('hrProcess.noActions')}</p>
      ) : (
        <div className={styles.actions}>
          {actions.map((action, index) => (
            <Button
              key={action}
              size="sm"
              variant={index === 0 ? 'primary' : 'secondary'}
              disabled={run.isPending}
              onClick={() => start(action)}
            >
              {t(`hrProcess.actions.${action}`)}
            </Button>
          ))}
        </div>
      )}

      {data.messages.length > 0 ? (
        <div className={styles.thread}>
          <p className={styles.sectionTitle}>{t('hrProcess.thread')}</p>
          {data.messages.map((message) => (
            <p key={`${message.createdAt}-${message.author}`} className={styles.message}>
              <span className={styles.messageAuthor}>
                {t(message.author === 'hr' ? 'hrProcess.authorHr' : 'hrProcess.authorEmployee')}
              </span>
              <span>{message.body}</span>
            </p>
          ))}
        </div>
      ) : null}

      {data.events.length > 0 ? (
        <div className={styles.events}>
          <p className={styles.sectionTitle}>{t('hrProcess.events')}</p>
          {/*
           * Etap w aktach to etap OSIĄGNIĘTY: trwa krok następny, a po
           * aktywacji wszystkie kroki są zrobione (Fala 6, grupa 88).
           */}
          <ProcessTimeline
            tone="onLight"
            current={data.awaitingMedicalApproval ? data.stageNumber : data.stageNumber + 1}
            blocked={data.sideState === 'rejected'}
            items={ONBOARDING_STAGES.map((stage) => ({
              key: stage,
              label: t(`onboardingStages.${stage}`),
              owner: t(`onboardingOwners.${stage}`),
            }))}
          />
          <ol className={styles.eventList}>
            {data.events.map((event, index) => {
              /*
               * Etykieta z AKCJI, nie z etapu docelowego: potwierdzenie badań
               * przez pracownika i zatwierdzenie przez kadry kończą się na tym
               * samym etapie 8, a to dwa różne fakty (grupa 88).
               */
              const action = typeof event.meta?.action === 'string' ? event.meta.action : null
              const stageLabel = t(`onboardingStages.${ONBOARDING_STAGES[event.toStage - 1]!}`)
              return (
                <li key={`${index}-${action ?? event.toStage}`} className={styles.event}>
                  <span className={styles.eventTime}>
                    {formatDate(event.createdAt, 'dot')}
                  </span>
                  <span className={styles.eventLabel}>
                    {action
                      ? t(`auditActions.onboarding.${action}`, { defaultValue: stageLabel })
                      : stageLabel}
                  </span>
                  <span className={styles.eventActor}>{event.actorLabel ?? '—'}</span>
                </li>
              )
            })}
          </ol>
        </div>
      ) : null}

      {/* ── Formularze akcji ─────────────────────────────────────────── */}

      <Modal
        open={openAction === 'return_data' || openAction === 'answer_question'}
        onClose={closeForm}
        title={t(openAction === 'answer_question' ? 'hrProcess.answerLabel' : 'hrProcess.returnTitle')}
        footer={
          <div className={styles.formActions}>
            <Button variant="secondary" onClick={closeForm}>
              {t('hrProcess.cancel')}
            </Button>
            <Button
              disabled={comment.trim().length === 0 || run.isPending}
              onClick={() => run.mutate(openAction!)}
            >
              {t(
                openAction === 'answer_question'
                  ? 'hrProcess.answerSubmit'
                  : 'hrProcess.returnSubmit',
              )}
            </Button>
          </div>
        }
      >
        {error !== null ? <InfoBanner tone="danger">{t(error)}</InfoBanner> : null}
        <FormField
          label={t(
            openAction === 'answer_question' ? 'hrProcess.answerLabel' : 'hrProcess.returnLabel',
          )}
          htmlFor={openAction === 'answer_question' ? ids.answer : ids.comment}
        >
          <textarea
            id={openAction === 'answer_question' ? ids.answer : ids.comment}
            className={styles.textarea}
            rows={4}
            placeholder={t(
              openAction === 'answer_question'
                ? 'hrProcess.answerPlaceholder'
                : 'hrProcess.returnPlaceholder',
            )}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
        </FormField>
      </Modal>

      <Modal
        open={openAction === 'issue_referral'}
        onClose={closeForm}
        title={t('hrProcess.referralTitle')}
        footer={
          <div className={styles.formActions}>
            <Button variant="secondary" onClick={closeForm}>
              {t('hrProcess.cancel')}
            </Button>
            <Button disabled={run.isPending} onClick={() => run.mutate('issue_referral')}>
              {t('hrProcess.referralSubmit')}
            </Button>
          </div>
        }
      >
        {error !== null ? <InfoBanner tone="danger">{t(error)}</InfoBanner> : null}
        <FormField label={t('hrProcess.referralDays')} htmlFor={ids.days}>
          <Input
            type="number"
            min={1}
            max={90}
            value={days}
            onChange={(event) => setDays(event.target.value)}
          />
        </FormField>
      </Modal>

      <Modal
        open={openAction === 'confirm_bhp'}
        onClose={closeForm}
        title={t('hrProcess.bhpTitle')}
        footer={
          <div className={styles.formActions}>
            <Button variant="secondary" onClick={closeForm}>
              {t('hrProcess.cancel')}
            </Button>
            <Button
              disabled={
                bhp.performedAt.length === 0 ||
                bhp.conductedBy.trim().length === 0 ||
                run.isPending
              }
              onClick={() => run.mutate('confirm_bhp')}
            >
              {t('hrProcess.bhpSubmit')}
            </Button>
          </div>
        }
      >
        {error !== null ? <InfoBanner tone="danger">{t(error)}</InfoBanner> : null}
        <p className={styles.formNote}>{t('hrProcess.bhpNote')}</p>
        <div className={styles.formGrid}>
          <FormField label={t('hrProcess.bhpDate')} htmlFor={ids.bhpDate}>
            <Input
              type="date"
              value={bhp.performedAt}
              onChange={(event) => setBhp({ ...bhp, performedAt: event.target.value })}
            />
          </FormField>
          <FormField label={t('hrProcess.bhpValidUntil')} htmlFor={ids.bhpValid}>
            <Input
              type="date"
              value={bhp.validUntil}
              onChange={(event) => setBhp({ ...bhp, validUntil: event.target.value })}
            />
          </FormField>
          <FormField
            className={styles.formFull}
            label={t('hrProcess.bhpConductedBy')}
            htmlFor={ids.bhpBy}
          >
            <Input
              value={bhp.conductedBy}
              onChange={(event) => setBhp({ ...bhp, conductedBy: event.target.value })}
            />
          </FormField>
        </div>
      </Modal>

      <Modal
        open={openAction === 'prepare_terms'}
        onClose={closeForm}
        title={t('hrProcess.termsTitle')}
        footer={
          <div className={styles.formActions}>
            <Button variant="secondary" onClick={closeForm}>
              {t('hrProcess.cancel')}
            </Button>
            <Button
              disabled={termsIncomplete || run.isPending}
              onClick={() => run.mutate('prepare_terms')}
            >
              {t('hrProcess.termsSubmit')}
            </Button>
          </div>
        }
      >
        {error !== null ? <InfoBanner tone="danger">{t(error)}</InfoBanner> : null}
        <p className={styles.formNote}>{t('hrProcess.termsSubtitle')}</p>
        <div className={styles.formGrid}>
          <FormField
            className={styles.formFull}
            label={t('hrProcess.termsPosition')}
            htmlFor={ids.position}
            {...termsIssue('position')}
          >
            <Input
              value={terms.position}
              onChange={(event) => setTerms({ ...terms, position: event.target.value })}
            />
          </FormField>
          <FormField
            label={t('hrProcess.termsContractType')}
            htmlFor={ids.contractType}
            {...termsIssue('contractType')}
          >
            <Select
              value={terms.contractType}
              onChange={(event) =>
                setTerms({ ...terms, contractType: event.target.value as ContractType | '' })
              }
            >
              <option value="">{t('hrProcess.termsContractTypeNone')}</option>
              {CONTRACT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`contractType.${type}`)}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField
            label={t('hrProcess.termsStartDate')}
            htmlFor={ids.startDate}
            {...termsIssue('startDate')}
          >
            <Input
              type="date"
              value={terms.startDate}
              onChange={(event) => setTerms({ ...terms, startDate: event.target.value })}
            />
          </FormField>
          {employmentTerms ? (
            <FormField label={t('hrProcess.termsFte')} htmlFor={ids.fte} {...termsIssue('fte')}>
              <Input
                type="number"
                min={0.1}
                max={1}
                step={0.05}
                value={terms.fte}
                onChange={(event) => setTerms({ ...terms, fte: event.target.value })}
              />
            </FormField>
          ) : null}
          {probationTerms ? (
            <FormField
              label={t('hrProcess.termsProbation')}
              htmlFor={ids.probation}
              {...termsIssue('probationMonths')}
            >
              <Input
                type="number"
                min={1}
                max={3}
                value={terms.probationMonths}
                onChange={(event) => setTerms({ ...terms, probationMonths: event.target.value })}
              />
            </FormField>
          ) : null}
          <FormField
            className={styles.formFull}
            label={t(salaryLabel)}
            htmlFor={ids.salary}
            {...termsIssue('baseSalaryGr')}
          >
            <Input
              inputMode="decimal"
              value={terms.baseSalaryZl}
              onChange={(event) => setTerms({ ...terms, baseSalaryZl: event.target.value })}
            />
          </FormField>
          <FormField
            className={styles.formFull}
            label={t('hrProcess.termsBonus')}
            htmlFor={ids.bonus}
          >
            <Input
              placeholder={t('hrProcess.termsBonusPlaceholder')}
              value={terms.bonusNote}
              onChange={(event) => setTerms({ ...terms, bonusNote: event.target.value })}
            />
          </FormField>
          <FormField label={t('hrProcess.termsWorkplace')} htmlFor={ids.workplace}>
            <Input
              value={terms.workplace}
              onChange={(event) => setTerms({ ...terms, workplace: event.target.value })}
            />
          </FormField>
          <FormField label={t('hrProcess.termsWorkMode')} htmlFor={ids.workMode}>
            <Input
              value={terms.workMode}
              onChange={(event) => setTerms({ ...terms, workMode: event.target.value })}
            />
          </FormField>
        </div>
        {termsIncomplete ? (
          <p className={cx(styles.formNote, styles.formWarn)}>{t('hrProcess.termsRequired')}</p>
        ) : null}
      </Modal>
    </Card>
  )
}
